import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Grid,
  CircularProgress,
  Typography,
  Paper,
  Snackbar,
  Tabs,
  Tab,
} from '@material-ui/core';
import ErrorIcon from '@material-ui/icons/Error';

import EmployeeAttributes from './EmployeeAttributes';
import NotesContainer from '../Note/NotesContainer';
import Dropzone from '../Dropzone';
import Availability from '../Availability/Availability';
import PositionAttributes from './PositionAttributes';
import ModelMeasurement from './ModelMeasurement';
import {
  addNote,
  deleteNote,
  fetchMe,
  fetchNotes,
  fetchStudioEmployee,
  sendAvailabilityEmail,
  updateModelMeasurement,
  updateNote,
  updatePositionAttributes,
  updateProfileImage,
} from '../../helpers/externalRequests';
import {
  isOverFourMonths,
  positionRequiresMeasurements,
} from '../../helpers/helpers';
import { prepareAvailabilityRequestData } from '../../helpers/bookingAvailabilityHelpers';
import '../../assets/stylesheets/UserProfileView.css';
import i18n from '../../i18n';
import { UserType } from '../../validation';
import isAuthEnabled from '../../helpers/authHelpers';

export const PROFILE = i18n.t('userProfile:profile');
export const POSITIONS = i18n.t('userProfile:positions');
export const AVAILABILITIES = i18n.t('userProfile:availabilities');
export const MEASUREMENTS = i18n.t('userProfile:measurement');

export class EmployeeProfile extends Component {
  constructor(props) {
    super(props);

    this.state = {
      employee: null,
      isFetchingData: true,
      updatedProfileImage: null,
      employeeNotes: [],
      selectedTab: PROFILE,
      snackOpen: false,
      snackbarMessage: '',
      isUploadingProfileImage: false,
      isModel: false,
      hasOutdatedMeasurement: false,
    };
  }

  handleUpdateProfileImage = async (imageFile) => {
    const { employee } = this.state;
    const data = new FormData();
    data.append('file', imageFile, imageFile.name);

    try {
      const response = await updateProfileImage(data, employee.id);
      this.setState({
        updatedProfileImage: response.data.profileImage,
        isUploadingProfileImage: false,
      });
    } catch (error) {
      this.setState({ isUploadingProfileImage: false });
      this.displaySnackbar(
        `${i18n.t('userProfile:errorUpdatingProfileImage')}: ${error}`
      );
    }
  };

  onFileAdded = (imageFile) => {
    if (!imageFile) {
      return this.displaySnackbar(i18n.t('userProfile:noFileAdded'));
    }
    if (imageFile.type !== 'image/jpeg' && imageFile.type !== 'image/png') {
      return this.displaySnackbar(i18n.t('userProfile:invalidImageType'));
    }
    return this.setState({ isUploadingProfileImage: true }, () =>
      this.handleUpdateProfileImage(imageFile)
    );
  };

  componentDidMount = () => {
    const { myProfile } = this.props;
    this.getEmployeeProfile(myProfile);
  };

  displaySnackbar = (message) => {
    this.setState({
      snackOpen: true,
      snackbarMessage: message,
    });
  };

  hideSnackbar = () => {
    this.setState({ snackOpen: false });
  };

  onSubmitModelMeasurementChange = async (editedMeasurement) => {
    const { employee } = this.state;
    const data = {
      editedMeasurement,
      newEntry: employee.newEntry,
      id: employee.id,
    };
    try {
      await updateModelMeasurement(data);
      const newEmployee = JSON.parse(JSON.stringify(employee));
      newEmployee.modelmeasurement = editedMeasurement;
      this.setState({ employee: newEmployee });
    } catch {
      this.displaySnackbar(i18n.t('userProfile:modelMeasurementError'));
    }
  };

  onSubmitPositionAttributeChange = async (
    positionId,
    abilityTier,
    bookingPriority,
    rate,
    extras
  ) => {
    const { employee } = this.state;

    const data = {
      id: employee.id,
      positionId,
      abilityTier,
      bookingPriority,
      rate,
      extras,
    };

    try {
      await updatePositionAttributes(data);
      this.getEmployee(employee.id);
      this.displaySnackbar(i18n.t('userProfile:editSuccess'));
    } catch {
      this.displaySnackbar(i18n.t('userProfile:editError'));
    }
  };

  getEmployeeProfile = (myProfile) => {
    let { employeeId } = this.props;
    if (myProfile || !employeeId) {
      const {
        user: { myEmployeeId },
      } = this.props;
      employeeId = myEmployeeId;
    }
    this.getEmployee(employeeId);
  };

  getEmployee = async (id) => {
    const {
      user: { email, myEmployeeId },
    } = this.props;
    try {
      const [employeeResults, notesReponse] = await Promise.all([
        id === myEmployeeId
          ? fetchMe(email, isAuthEnabled)
          : fetchStudioEmployee(id),
        fetchNotes(id),
      ]);
      this.setState(
        {
          isFetchingData: false,
          employee: employeeResults.data,
          employeeNotes: notesReponse.data.allNotes,
          loggedInEmployeeId: myEmployeeId,
        },
        () => {
          const { employee } = this.state;
          this.displayModelMeasurementTab(employee);
        }
      );
    } catch (error) {
      this.displaySnackbar(
        `${i18n.t('userProfile:errorRetrievingData')} ${error}`
      );
    }
  };

  onNoteSubmit = async (description) => {
    const { employee } = this.state;
    const {
      user: { myEmployeeId },
    } = this.props;
    const newNote = {
      description,
      targetStudioEmployeeId: employee.id,
      authorStudioEmployeeId: myEmployeeId,
    };

    try {
      await addNote(newNote);
      this.getAllEmployeeNotes();
    } catch (error) {
      this.displaySnackbar(
        `${i18n.t('userProfile:errorAddingNotes')} ${error}`
      );
    }
  };

  onNoteDelete = async (id) => {
    try {
      await deleteNote({ noteId: id });
      this.getAllEmployeeNotes();
    } catch (error) {
      this.displaySnackbar(
        `${i18n.t('userProfile:errorDeletingNotes')} ${error}`
      );
    }
  };

  updateNoteDescription = async (id, description) => {
    try {
      await updateNote({ id, description });
      this.getAllEmployeeNotes();
    } catch (error) {
      this.displaySnackbar(
        `${i18n.t('userProfile:errorUpdatingNotes')} ${error}`
      );
    }
  };

  getEmployeePositions = () => {
    const { employee } = this.state;
    return employee.positions.map((position) => position.positionName);
  };

  getAllEmployeeNotes = async () => {
    const { employee } = this.state;
    const {
      user: { myEmployeeId },
    } = this.props;
    try {
      const response = await fetchNotes(employee.id);
      this.setState({
        employeeNotes: response.data.allNotes,
        loggedInEmployeeId: myEmployeeId,
      });
    } catch (error) {
      this.displaySnackbar(
        `${i18n.t('userProfile:errorRetrievingNotes')} ${error}`
      );
    }
  };

  handleRequestAvailability = async (month, year) => {
    const { employee } = this.state;
    const availabilityRequestData = prepareAvailabilityRequestData(
      employee,
      month,
      year
    );
    try {
      await Promise.all(
        availabilityRequestData.map((data) => sendAvailabilityEmail(data))
      );
      this.displaySnackbar(i18n.t('userProfile:availabilityRequestSuccess'));
    } catch {
      this.displaySnackbar(i18n.t('userProfile:availabilityRequestError'));
    }
  };

  getTabToDisplay = (employee) => {
    if (!employee) {
      return (
        <Paper className="noEmployeeFoundContainer">
          <ErrorIcon />
          <Typography variant="body1">{i18n.t('noEmployeeFound')}</Typography>
        </Paper>
      );
    }
    const { selectedTab } = this.state;
    switch (selectedTab) {
      case AVAILABILITIES:
        return (
          <Availability
            employee={employee}
            studioEmployeeId={employee.id}
            isFullTime={employee.isFullTime}
            roles={employee.roles}
            positions={employee.positions}
            handleRequestAvailability={this.handleRequestAvailability}
          />
        );
      case POSITIONS:
        return (
          <PositionAttributes
            displaySnackbar={this.displaySnackbar}
            positions={employee.positions}
            onSubmitPositionAttributeChange={
              this.onSubmitPositionAttributeChange
            }
            employee={employee}
          />
        );
      case MEASUREMENTS:
        return (
          <ModelMeasurement
            modelmeasurement={employee.modelmeasurement}
            onSubmitModelMeasurementChange={this.onSubmitModelMeasurementChange}
          />
        );
      case PROFILE:
      default:
        return (
          <EmployeeAttributes
            employee={employee}
            getEmployee={this.getEmployee}
            displaySnackbar={this.displaySnackbar}
          />
        );
    }
  };

  toggleTab = (event, value) => {
    this.setState({
      selectedTab: value,
    });
  };

  displayModelMeasurementTab = (employee) => {
    const isModel = positionRequiresMeasurements(employee.positions);
    const hasOutdatedMeasurement =
      employee.modelmeasurement &&
      Object.keys(employee.modelmeasurement).some((key) =>
        isOverFourMonths(
          employee.modelmeasurement[key][`${key}LastUpdatedAt`],
          new Date()
        )
      ) || false;
    this.setState({
      isModel,
      hasOutdatedMeasurement,
    });
  };

  render() {
    const {
      employee,
      employeeNotes,
      loggedInEmployeeId,
      updatedProfileImage,
      isFetchingData,
      isUploadingProfileImage,
      selectedTab,
      snackOpen,
      snackbarMessage,
      isModel,
      hasOutdatedMeasurement,
    } = this.state;

    const displayTab = this.getTabToDisplay(employee);

    // Employee data not yet fetched
    if (isFetchingData) {
      return (
        <>
          <CircularProgress color="secondary" />
          <Typography variant="body1">{i18n.t('loading')}</Typography>
        </>
      );
    }
    // Employee data was fetched, but no employee of this ID was found
    if (!employee) {
      return (
        <Paper className="noEmployeeFoundContainer">
          <ErrorIcon />
          <Typography variant="body1">{i18n.t('noEmployeeFound')}</Typography>
        </Paper>
      );
    }

    // Employee found and loaded
    const profileImage = updatedProfileImage || employee.profileImage;
    return (
      <div className="userProfileContainer">
        <Grid container spacing={2}>
          <Grid item md={4}>
            <Dropzone
              profileImage={profileImage}
              disabled={false}
              onFileAdded={this.onFileAdded}
              loading={isUploadingProfileImage}
            />
            <NotesContainer
              employeeId={employee.studioEmployeeId}
              loggedInEmployeeId={loggedInEmployeeId}
              notes={employeeNotes}
              onNoteSubmit={this.onNoteSubmit}
              onNoteDelete={this.onNoteDelete}
              updateNoteDescription={this.updateNoteDescription}
            />
          </Grid>
          <Grid item md={8}>
            <Paper>
              <Tabs
                value={selectedTab}
                onChange={this.toggleTab}
                indicatorColor="secondary"
                textColor="secondary"
                centered
                variant="fullWidth"
              >
                <Tab value={PROFILE} label={PROFILE} className="tabItem" />
                <Tab value={POSITIONS} label={POSITIONS} className="tabItem" />
                <Tab
                  value={AVAILABILITIES}
                  label={AVAILABILITIES}
                  className="tabItem"
                />
                {isModel && (
                  <Tab
                    value={MEASUREMENTS}
                    label={MEASUREMENTS}
                    className={
                      hasOutdatedMeasurement ? 'outdatedMeasurement' : 'tabItem'
                    }
                  />
                )}
              </Tabs>
            </Paper>
            {displayTab}
          </Grid>

          <Snackbar
            message={snackbarMessage}
            open={snackOpen}
            onClose={this.hideSnackbar}
            autoHideDuration={2000}
          />
        </Grid>
      </div>
    );
  }
}

EmployeeProfile.defaultProps = {
  myProfile: false,
  employeeId: null,
};

EmployeeProfile.propTypes = {
  employeeId: PropTypes.number,
  user: UserType.isRequired,
  myProfile: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  user: state.user,
});

export { EmployeeProfile as EmployeeProfileNoConnect };
export default connect(mapStateToProps)(EmployeeProfile);
